import {votingApi} from '@src/api/voting-api';
import {PaginationLogic} from '@src/commonlogic/PaginationLogic';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

export const votingKeys = {
  votingListDetailsRoot: () => ['votingListDetails'],
  votingListDetails: (id: string) => ['votingListDetails', id],
  votingList: (debouncedSearch: string) => ['votingList', debouncedSearch],
  eventVotingList: (eventId: string) => ['votingList', 'event', eventId],
  liveDetails: (id: string) => ['liveDetails', id],
};

export function useVotingChangeOrderMutation(options?: {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: votingApi.changeVotesOrderReq) =>
      votingApi.changeVotesOrder(body),
    onSuccess: () => {
      queryClient.invalidateQueries(['votingList']);
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

export function useVotingLiveDetailsQuery(id: string) {
  return useQuery({
    queryKey: votingKeys.liveDetails(id),
    queryFn: () => votingApi.getVoteLiveDetailsById(id),
    refetchInterval: 5000,
  });
}

export function useVotingListDetailsQuery(id: string, options?: unknown) {
  return useQuery({
    queryKey: votingKeys.votingListDetails(id),
    queryFn: () => votingApi.getVoteDetailsById(id),
    enabled: !!id,
    ...(options as Record<string, unknown>),
  });
}

export function useAllVotesInfinityQuery(
  id: string,
  debouncedSearch: string,
  isAdmin: boolean,
  isArchive = false,
  isMy = true
) {
  return useInfiniteQuery(
    ['votingList', debouncedSearch, isArchive, isMy],
    ({pageParam = 1}) =>
      isAdmin
        ? votingApi.getAllVotesAdmin({
            page: pageParam,
            query: debouncedSearch,
            isArchived: isArchive,
            limit: 20,
          })
        : votingApi.getAllCompanyVotes(id as string, {
            page: pageParam,
            isArchived: isArchive,
            limit: 20,
            isMy,
          }),
    {
      getNextPageParam: PaginationLogic.handleGetNextParam,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      cacheTime: 0,
      staleTime: 0,
    }
  );
}

export function useStartVotingMutation(
  votingId: string,
  options?: {
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
  }
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => votingApi.startVoting(votingId),
    onSuccess: () => {
      queryClient.invalidateQueries(votingKeys.votingList('')[0]);
      queryClient.invalidateQueries({
        queryKey: votingKeys.votingListDetails(votingId),
      });
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

export function useEditVotingMutation(options?: {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: votingApi.editVoteReq) => votingApi.editVote(body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['votingList'],
      });
      queryClient.invalidateQueries({
        queryKey: ['liveDetails'],
      });
      queryClient.invalidateQueries({
        queryKey: votingKeys.votingListDetails(variables.voteId),
      });
      queryClient.invalidateQueries({
        queryKey: ['event'],
      });
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

export function useSendVotingImageMutation(options?: {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (formData: FormData) => votingApi.sendVotingImage(formData),
    onSuccess: () => {
      queryClient.invalidateQueries(['votingList']);
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}
